import { useMemo } from "react";
import { GridDataTableColumn } from "../GridDataTable/GridDataTableColumn";

export const HiddenDefaultFilterBoolean = [
  "contains",
  "greater",
  "greater-equal",
  "less",
  "less-equal",
  "starts-with",
  "ends-with",
];
export const HiddenDefaultFilterString = [
  "greater",
  "greater-equal",
  "less",
  "less-equal",
];
export const HiddenDefaultFilterDateNumber = [
  "contains",
  "starts-with",
  "ends-with",
];

/** The shape of a single entry in the `filterColumnTypes` record. */
export interface FilterColumnTypeEntry {
  type?: "text" | "date" | "select";
  options?: Array<{ value: string | number; label: string }>;
  apiCallback?: (
    searchText: string
  ) => Promise<Array<{ value: string | number; label: string }>>;
}

/** The result returned by `useDataTableFilterColumnTypeMapper`. */
export type FilterColumnTypesMap = Record<string, FilterColumnTypeEntry>;

/**
 * Options accepted by `useDataTableFilterColumnTypeMapper`.
 */
export interface UseDataTableFilterColumnTypeMapperOptions {
  /** The column definitions that may contain a `type` property. */
  columns: GridDataTableColumn[];
}

/**
 * Determines the appropriate hidden-filter list for a column based on
 * its `type`.  Returns `undefined` when no filtering is needed.
 */
function getHiddenDefaultFilters(type?: string): string[] | undefined {
  if (!type) return HiddenDefaultFilterString;
  if (type === "date" || type === "number")
    return HiddenDefaultFilterDateNumber;
  if (type === "boolean") return HiddenDefaultFilterBoolean;
  return undefined;
}

/**
 * React hook that iterates through an array of `GridDataTableColumn`
 * definitions and, for every column that has a `type` defined, builds
 * a `filterColumnTypes` record suitable for passing to `useDataTable`.
 *
 * The result is memoized — re-computation only occurs when the `columns`
 * array reference changes.
 *
 * **Side-effect:** The hook also sets `hiddenDefaultFilters` on each
 * column according to its `type`.  This mutates the column objects in
 * place because the DataTable reads those properties directly.
 *
 * @example
 * ```tsx
 * const filterColumnTypes = useDataTableFilterColumnTypeMapper({ columns });
 *
 * const tableState = useDataTable({
 *   initialColumns: columns,
 *   showFilterDataset: true,
 *   filterColumnTypes,
 * });
 * ```
 */
export function useDataTableFilterColumnTypeMapper({
  columns,
}: UseDataTableFilterColumnTypeMapperOptions): FilterColumnTypesMap {
  return useMemo(() => {
    const map: FilterColumnTypesMap = {};

    const selectOptions = [
      { value: "true", label: "True" },
      { value: "false", label: "False" },
    ];

    for (const column of columns) {
      if (column.accessor) {
        const accessor =
          typeof column.accessor === "string"
            ? column.accessor
            : String(column.accessor);

        if (column.type) {
          map[accessor] = {
            type: column.type as FilterColumnTypeEntry["type"],
          };
        }

        if (column.type === "boolean") {
          map[accessor].type = "select";
          map[accessor].options = selectOptions
        }

        // Set hiddenDefaultFilters based on the column type.
        const hidden = getHiddenDefaultFilters(column.type);
        if (hidden) {
          column.hiddenDefaultFilters = hidden;
        }
      }
    }

    return map;
  }, [columns]);
}
